// src/pages/TrackCreation/ExtraScreen.tsx
import React from "react";
import { useTranslation } from "react-i18next";
import { useGlobalState } from "../../../../context/useGlobalState";
import Dropdown from "../../../../components/common/Dropdown";
import NumberInput from "../../../../components/common/NumberInput";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import TextInput from "../../../../components/ui/TextInput";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import Button from "../../../../components/ui/Button";
import { ExtraScreenProps } from "../../../../types/screensTypes";
import RadioButton from "../../../../components/common/RadioButton";
import ToggleSwitch from "../../../../components/common/ToggleSwitch";
import activeRadio from "../../../../assets/images/radio_active.png";
import inactiveRadio from "../../../../assets/images/radio_inactive.png";
import stepStyles from "../createStep.module.css";
import styles from "./Extra.module.css";

const ExtraScreen: React.FC<ExtraScreenProps> = ({ navigateToNext }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data, loading, error } = useGlobalState();
    const { trackData, updateTrackData } = useTrackCreation();

    // Check if the current filter has hasOnlyImageScanWaypoints
    const currentFilter = data?.filtersAI?.find((f: any) => f.value === trackData?.subType);
    const isImageScan = currentFilter?.hasOnlyImageScanWaypoints || false;

    // Determine if ratings are enabled (numberOfListeningsBeforeRating is not null)
    const isRatingsEnabled =
        trackData.numberOfListeningsBeforeRating !== null && trackData.numberOfListeningsBeforeRating !== undefined;

    const handleRatingsToggle = (enabled: boolean) => {
        if (enabled) {
            // Enable ratings with default of 4 listenings
            updateTrackData("numberOfListeningsBeforeRating", 4);
        } else {
            // Disable ratings by setting to null explicitly
            updateTrackData("numberOfListeningsBeforeRating", null);
            updateTrackData("ratingLink", "");
        }
    };

    const handleRatingLinkUpdate = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        updateTrackData("ratingLink", e.target.value);
        // Ensure numberOfListeningsBeforeRating has a value when rating link is set
        if (!trackData.numberOfListeningsBeforeRating) {
            updateTrackData("numberOfListeningsBeforeRating", 4);
        }
    };

    return (
        <div className={`step-container ${stepStyles.stepContainer} ${stepStyles.step} ${styles.root}`}>
            <TitleSubtitle
                title={t("inWhichPositionWouldYouLikeYourAudioTourToBeDisplayed")}
                subtitle={t(
                    "ifYouHaveSeveralAudioToursAvailableInTheAppChooseThePositionInWhichThisTourWillAppearFromTheList",
                )}
                layout="extra"
            />

            <NumberInput
                placeholder={t("selectNumber")}
                value={trackData.position ? trackData.position : ""}
                size="medium"
                onChange={(e) => updateTrackData("position", e.target.value)}
                min={1}
            />

            <TitleSubtitle
                title={t("durationOfTheAudioTour")}
                subtitle={t("indicateTheDurationOfYourAudioTour")}
                layout="extra"
            />

            <NumberInput
                placeholder={t("writeTheDurationInMinutes")}
                value={trackData.duration || ""}
                size="medium"
                onChange={(e) => updateTrackData("duration", e.target.value)}
            />

            <TitleSubtitle
                title={t("durationFreeTextTitle") || "Duration free text (optional, this language)"}
                subtitle={
                    t("durationFreeTextSubtitle") ||
                    'Overrides the numeric duration for this language only (e.g. "Libre"). Leave empty to display the duration in minutes.'
                }
                layout="extra"
            />

            <TextInput
                placeholder={t("durationFreeTextPlaceholder") || "e.g. Libre"}
                value={trackData.durationText || ""}
                size="medium"
                onChange={(e) => updateTrackData("durationText", e.target.value)}
            />

            <TitleSubtitle
                title={t("departureFreeTextTitle") || "Departure free text (optional, this language)"}
                subtitle={
                    t("departureFreeTextSubtitle") ||
                    'Overrides the starting point for this language only (e.g. "Où vous voulez dans le Safari"). Leave empty to display the address.'
                }
                layout="extra"
            />

            <TextInput
                placeholder={t("departureFreeTextPlaceholder") || "e.g. Où vous voulez dans le Safari"}
                value={trackData.departureText || ""}
                size="medium"
                onChange={(e) => updateTrackData("departureText", e.target.value)}
            />

            {trackData.type === "iduniquewalk" && (
                <>
                    <TitleSubtitle
                        title={t("lengthOfTheAudioTour")}
                        subtitle={t("indicateTheLengthOfYourAudioTour")}
                        layout="extra"
                    />

                    <NumberInput
                        placeholder={t("writeTheLengthOfTheTourInKms")}
                        value={trackData.length || ""}
                        size="medium"
                        onChange={(e) => updateTrackData("length", e.target.value)}
                    />
                </>
            )}

            <TitleSubtitle title={t("enableRatingsTitle")} subtitle={t("enableRatingsSubtitle")} layout="extra" />

            <div className={styles.toggleRow}>
                <ToggleSwitch checked={isRatingsEnabled} onChange={handleRatingsToggle} />
                <span className={styles.toggleLabel}>
                    {isRatingsEnabled
                        ? t("ratingsAfterListenings", { count: trackData.numberOfListeningsBeforeRating || 4 })
                        : t("ratingLinkTitle")}
                </span>
            </div>

            {isRatingsEnabled && (
                <>
                    <TitleSubtitle title={t("ratingLinkTitle")} subtitle={t("ratingLinkSubtitle")} layout="extra" />

                    <TextInput
                        placeholder={t("ratingLinkPlaceholder")}
                        value={trackData.ratingLink || ""}
                        size="medium"
                        onChange={handleRatingLinkUpdate}
                    />

                    <TitleSubtitle
                        title={t("numberOfListeningsBeforeRatingkTitle")}
                        subtitle={t("numberOfListeningsBeforeRatingSubtitle")}
                        layout="extra"
                    />

                    <NumberInput
                        value={trackData.numberOfListeningsBeforeRating || 4}
                        size="medium"
                        min={1}
                        onChange={(e) => updateTrackData("numberOfListeningsBeforeRating", e.target.value)}
                    />

                    <TitleSubtitle
                        title={t("hideTrackRatingsTitle") || "Hide ratings on the mobile UI"}
                        subtitle={
                            t("hideTrackRatingsSubtitle") ||
                            "Users can still rate the tour, but the rating stars will not be displayed on the track presentation thumbnails in the mobile app"
                        }
                        layout="extra"
                    />

                    <div className={styles.toggleRow}>
                        <ToggleSwitch
                            checked={trackData.hideTrackRatings || false}
                            onChange={(enabled) => updateTrackData("hideTrackRatings", enabled)}
                        />
                        <span className={styles.toggleLabel}>
                            {trackData.hideTrackRatings
                                ? t("hideTrackRatingsEnabled") || "Rating stars are hidden in the app"
                                : t("hideTrackRatingsDisabled") || "Rating stars are displayed in the app"}
                        </span>
                    </div>
                </>
            )}

            {/* Display Waypoint Index Numbers option - only for non-interior, non-image-scan tracks */}
            {trackData.type !== "iduniquemusee" && !isImageScan && (
                <>
                    <TitleSubtitle
                        title={t("displayWaypointIndexNumbersTitle") || "Display Waypoint Numbers on Map"}
                        subtitle={
                            t("displayWaypointIndexNumbersSubtitle") ||
                            "Show sequential numbers (1, 2, 3...) on waypoints displayed on the map"
                        }
                        layout="extra"
                    />

                    <div className={styles.toggleRow}>
                        <ToggleSwitch
                            checked={trackData.displayWaypointIndexNumbers || false}
                            onChange={(enabled) => updateTrackData("displayWaypointIndexNumbers", enabled)}
                        />
                        <span className={styles.toggleLabel}>
                            {trackData.displayWaypointIndexNumbers
                                ? t("displayWaypointIndexNumbersEnabled") ||
                                  "Waypoint numbers will be displayed on the map"
                                : t("displayWaypointIndexNumbersDisabled") || "Waypoint numbers will not be displayed"}
                        </span>
                    </div>
                </>
            )}

            {!isImageScan && (
                <>
                    <TitleSubtitle title={t("nameOfTheGuide")} subtitle={t("selectTheNameOfTheGuide")} layout="extra" />

                    <Dropdown
                        label=""
                        options={data.guides}
                        onChange={(value: string) => {
                            updateTrackData("guide", value);
                        }}
                        value={trackData.guide}
                        placeholder={t("selectAnOption")}
                        loading={loading}
                        error={error ? true : false}
                    />
                </>
            )}

            {trackData.type !== "iduniquemusee" && !isImageScan && (
                <>
                    <TitleSubtitle title={t("hasRoutesTitle")} subtitle={t("hasRoutesSub")} layout="extra" />

                    <div className={styles.radioGroup}>
                        <RadioButton
                            label={t("yes")}
                            subtitle={t("hasRoutesTrue")}
                            checked={trackData.isWithoutRoutes === false}
                            onChange={() => updateTrackData("isWithoutRoutes", false)}
                            activeImage={activeRadio}
                            inactiveImage={inactiveRadio}
                        />
                        <RadioButton
                            label={t("no")}
                            subtitle={t("hasRoutesFalse")}
                            checked={trackData.isWithoutRoutes === true}
                            onChange={() => updateTrackData("isWithoutRoutes", true)}
                            activeImage={activeRadio}
                            inactiveImage={inactiveRadio}
                        />
                    </div>
                </>
            )}

            {/*<TitleSubtitle title={t("isStartAnywhereTitle")} subtitle={t("isStartAnywhereSub")}  layout="extra" />*/}

            {/*<div style={{ marginTop: 20 }}>
        <RadioButton
          label={t("yes")}
          subtitle={t("isStartAnywhereTrue")}
          checked={trackData.isStartAnywhere === true}
          onChange={() => updateTrackData("isStartAnywhere", true)}
          activeImage={activeRadio}
          inactiveImage={inactiveRadio}
        />
        <RadioButton
          label={t("no")}
          subtitle={t("isStartAnywhereFalse")}
          checked={trackData.isStartAnywhere === false}
          onChange={() => updateTrackData("isStartAnywhere", false)}
          activeImage={activeRadio}
          inactiveImage={inactiveRadio}
        />
      </div>*/}

            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={t("continue")}
                    onClick={navigateToNext}
                    disabled={
                        !trackData.city ||
                        !trackData.position ||
                        (!trackData.duration && !trackData.durationText?.trim())
                    }
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                />
            </div>
        </div>
    );
};

export default ExtraScreen;
